import React from "react";
import { Box } from "@mui/material";
import useStyles from "../family-tax-credit-application-print/family-tax-credit-application-print.style";
import ApplicationCheckBox from "my-tax/components/application-check-box";
import CorporateSignature from "my-tax/components/corporate-signature";
import MyTaxDependentsInfoTableRow from "my-tax/components/dependents-info-table-row";
import { DOTTED_LINE } from "my-tax/constants";
import formatTaxNumber from "oc/utils/format-tax-number";

interface CombinedTaxCreditApplicationPrintProps {
  fullName?: string;
  taxNumber?: string;
  applicationDate?: string;
  aloneOrWithSpouse?: string;
  employerName?: string;
  employerTaxNumber?: string;
  states?: {
    spouseTaxNumber?: string;
    spouseEmployerName?: string;
    spouseEmployerTaxNumber?: string;
    dateOfDeclaration?: string;
    dependents?: Array<{
      dependentType?: string;
      name?: string;
      taxNumber?: string;
      entitlementTitle?: string;
      placeOfBirth?: string;
      dateOfBirth?: string;
      mothersName?: string;
      childType?: string;
    }>;
    applyOnHungary?: boolean;
    noRequestRelief?: boolean;
    taxReliefAmount?: string;
    taxReliefNumberOfChild?: string;
    spouseName?: string;
    isEligibleTwo?: boolean;
    isEligibleThree?: boolean;
    isEligible?: boolean;
  };
}

export const MyTaxCombinedTaxCreditApplicationPrint = React.forwardRef(
  (props: CombinedTaxCreditApplicationPrintProps, ref) => {
    const {
      fullName = "Minta Borbála",
      taxNumber = "8124440505",
      applicationDate,
      aloneOrWithSpouse,
      employerName,
      employerTaxNumber,
      states,
    } = props;
    const spouseTaxNumber = states?.spouseTaxNumber || "";
    const spouseCompanyName = states?.spouseEmployerName || "";
    const spouseCompanyTaxNumber =
      formatTaxNumber(states?.spouseEmployerTaxNumber) || "";
    let thisYear = states?.dateOfDeclaration
      ? new Date(states?.dateOfDeclaration).getFullYear().toString()
      : "";
    const classes = useStyles();

    return (
      <Box ref={ref}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          fontFamily="EB Garamond"
          fontSize="15px"
          style={{ paddingTop: "30px" }}
        >
          <Box
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Összevont adóelőleg-nyilatkozat a családi kedvezmény és a két, a három, illetve a négy vagy
            <Box />
            több gyermeket nevelő anyák kedvezménye érvényesítéséről
            <Box />
            <Box
              key="nyilatkozat-ev"
              display="flex"
              flexDirection="column"
              alignItems="center"
              paddingTop="2px"
            >
              A nyilatkozat benyújtásának éve: {thisYear}
            </Box>
          </Box>
          <Box className={classes.customBox}>
            <Box className={classes.separator} />
            <Box height="12px" />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
            >
              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  fontWeight="normal"
                >
                  A nyilatkozó magánszemély neve:
                  <Box margin="0px 5px">{fullName}</Box>
                  és
                </Box>
                <Box display="flex">
                  adóazonosító jele:
                  <Box margin="0px 5px">
                    {taxNumber ? taxNumber : DOTTED_LINE}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box height="12px" />
            <Box className={classes.separator} />
            <Box height="5px" />
            <Box display="flex">
              <Box className={classes.startChapter}>1.</Box>
              <Box fontWeight="normal" display="flex">
                Nyilatkozom, hogy jogosult vagyok, és igénybe kívánom venni a családi kedvezményt és
              </Box>
            </Box>
            <Box marginLeft="30px">
              <Box fontWeight="normal" display="flex">
                - a két gyermeket nevelő anyák kedvezményét
                <ApplicationCheckBox value={states?.isEligibleTwo} />
                &nbsp;,
              </Box>
              <Box fontWeight="normal" display="flex">
                - a három gyermeket nevelő anyák kedvezményét
                <ApplicationCheckBox value={states?.isEligibleThree} />
                &nbsp;,
              </Box>
              <Box fontWeight="normal" display="flex">
                - a négy vagy több gyermeket nevelő anyák kedvezményét
                <ApplicationCheckBox value={states?.isEligible} />
                &nbsp;,
              </Box>
            </Box>
            <Box marginLeft="30px" fontWeight="normal">
              és külföldi államban a jövedelmem után azonos vagy hasonló kedvezményeket nem veszek (vettem) igénybe.
            </Box>
            <Box height="5px" />
            <Box className={classes.separator} />
            <Box height="5px" />
            <Box display="flex">
              <Box className={classes.startChapter}>2.</Box>
              <Box fontWeight="normal" display="flex">
                Az eltartottak adatai:
              </Box>
            </Box>
            <Box height="5px" />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-around"
            >
              <Box width="120px" textAlign="center" marginLeft="5px">
                <Box className={classes.tableTitle}>Adóazonosító jel</Box>
              </Box>
              <Box width="140px" textAlign="center">
                <Box className={classes.tableTitle}>Név</Box>
              </Box>
              <Box width="40px" textAlign="center">
                <Box className={classes.tableTitle}>EM*</Box>
              </Box>
              <Box width="40px" textAlign="center" marginLeft="10px">
                <Box className={classes.tableTitle}>JJ**</Box>
              </Box>
              <Box width="40px" textAlign="center" marginLeft="10px">
                <Box className={classes.tableTitle}>TA***</Box>
              </Box>
              <Box width="150px" textAlign="center" marginLeft="10px">
                <Box className={classes.tableTitle}>Születési hely, idő</Box>
              </Box>
            </Box>
            {states?.dependents &&
              (states?.dependents ?? []).map((item, index) => (
                <MyTaxDependentsInfoTableRow
                  key={`dependent_${index}`}
                  variant="combined"
                  dependentQuality={item.dependentType || ""}
                  name={item.name || ""}
                  taxNumber={item.taxNumber}
                  dependencyTitle={item.entitlementTitle}
                  changeDate={item.childType}
                  placeOfBirth={item.placeOfBirth}
                  dateOfBirth={item.dateOfBirth}
                />
              ))}
            <Box height="5px" />
            <Box>EM* - Eltartotti minőség</Box>
            <Box height="2px" />
            <Box>JJ** - Jogosultság jogcímei</Box>
            <Box height="2px" />
            <Box>TA*** - Több gyermeket nevelő anyák kedvezménye</Box>
            <Box height="5px" />
            <Box className={classes.separator} />
            <Box height="5px" />
            <Box>
              <Box display="flex">
                <Box className={classes.startChapter}>3.</Box>
                <Box fontWeight="normal" display="flex">
                  Nyilatkozom, hogy a családi kedvezményt{" "}
                  <b style={{ marginLeft: "5px" }}>egyedül</b>
                  <ApplicationCheckBox
                    value={aloneOrWithSpouse === "alone" ? true : false}
                  />
                  ,
                  <Box width="3px" />
                  vagy jogosult házastársammal/élettársammal{" "}
                  <b style={{ marginLeft: "5px" }}>közösen</b>
                  <ApplicationCheckBox
                    value={aloneOrWithSpouse === "withSpouse" ? true : false}
                  />
                </Box>
              </Box>
              <Box marginLeft="40px">érvényesítem.</Box>
            </Box>
            <Box height="10px" />
            <Box display="flex">
              <Box className={classes.startChapter}>4.</Box>
              <Box fontWeight="normal" display="flex">
                Nyilatkozom, hogy nem kérem a családi járulékkedvezmény havi összegének érvényesítését.
                <ApplicationCheckBox value={states?.noRequestRelief} />
              </Box>
            </Box>
            <Box height="10px" />
            <Box display="flex">
              <Box className={classes.startChapter}>5.</Box>
              <Box display="flex" alignItems="center">
                A fentiek alapján nyilatkozom, hogy a családi kedvezményt (az a)
                vagy b) sor egyikét töltse ki!)
              </Box>
            </Box>
            <Box height="5px" />
            <Box display="flex" marginLeft="60px">
              <Box style={{ fontStyle: "italic" }}>a.</Box>
              <Box width="80px" style={{ textAlign: "center" }}>
                {states?.taxReliefAmount ? states?.taxReliefAmount : "-"}
              </Box>
              <b>forint összegben kívánom igénybe venni.</b>
            </Box>
            <Box height="5px" />
            <Box display="flex" marginLeft="60px">
              <Box style={{ fontStyle: "italic" }}>b.</Box>
              <Box width="80px" style={{ textAlign: "center" }}>
                {states?.taxReliefNumberOfChild
                  ? states?.taxReliefNumberOfChild
                  : "-"}
              </Box>
              <b>fő kedvezményezett eltartott után kívánom igénybe venni.</b>
            </Box>
            <Box height="10px" />
            <Box display="flex">
              <Box className={classes.startChapter}>6.</Box>
              <Box fontWeight="normal">
                Felelősségem tudatában kijelentem, hogy a nyilatkozat tartalmát
                házastársam, élettárs megismerte és elfogadta.
              </Box>
            </Box>
            <Box height="10px" />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginLeft="40px"
            >
              <Box
                style={{
                  display: "flex",
                }}
              >
                Kelt:
                <Box width="150px" textAlign="center">
                  {applicationDate}
                </Box>
              </Box>
            </Box>
            <Box height="5px" />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginLeft="40px"
            >
              A nyilatkozatot tevő magánszemély aláírása:
              <Box marginRight="40px">
                .............................................................
              </Box>
            </Box>
            <Box height="5px" />
            <Box className={classes.separator} />
            <Box height="5px" />
            <Box display="flex">
              <Box className={classes.startChapter}>7.</Box>
              <Box fontWeight="normal">
                A családi kedvezményt kérő magánszemély jogosult házastársa/élettársa
              </Box>
            </Box>
            <Box>
              <Box
                display="flex"
                alignItems="center"
                style={{
                  margin: "15px 0px 5px 20px",
                }}
              >
                neve:
                <Box width="200px" textAlign="center">
                  {aloneOrWithSpouse === "alone"
                    ? DOTTED_LINE
                    : states?.spouseName
                      ? states?.spouseName
                      : DOTTED_LINE}
                  ,
                </Box>
                <Box
                  style={{
                    marginLeft: "100px",
                  }}
                >
                  adóazonosító jele:
                </Box>
                <Box marginLeft="20px">
                  {aloneOrWithSpouse === "alone"
                    ? DOTTED_LINE
                    : spouseTaxNumber
                      ? spouseTaxNumber
                      : DOTTED_LINE}
                </Box>
              </Box>
            </Box>
            <Box height="5px" />
            <Box display="flex" marginLeft="25px">
              adóelőleget megállapító munkáltatója, kifizetője megnevezése:
              <Box marginLeft="20px"></Box>
              {aloneOrWithSpouse === "alone"
                ? DOTTED_LINE
                : spouseCompanyName
                  ? spouseCompanyName
                  : DOTTED_LINE}
            </Box>
            <Box height="10px" />
            <Box display="flex" marginLeft="25px">
              adószáma:
              <Box marginLeft="20px" display="initial"></Box>
              {aloneOrWithSpouse === "alone"
                ? DOTTED_LINE
                : spouseCompanyTaxNumber
                  ? spouseCompanyTaxNumber
                  : DOTTED_LINE}
            </Box>
            <Box height="10px" />
            <Box className={classes.separator} />
            <Box height="10px" />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box fontWeight="normal">
                A nyilatkozó magánszemély munkáltatójaként a nyilatkozat
                tartalmát tudomásul vettem. A magánszemély adóelőlegét a
                nyilatkozat figyelembevételével állapítom meg.
              </Box>
            </Box>
            <Box height="15px" />
            <Box display="flex" alignItems="center">
              A munkáltató, kifizető megnevezése:
              <Box width="250px" textAlign="center">
                {employerName ? employerName : DOTTED_LINE}
              </Box>
            </Box>
            <Box height="10px" />
            <Box display="flex" alignItems="center">
              adószáma:
              <Box marginLeft="25px" textAlign="center">
                {employerTaxNumber
                  ? formatTaxNumber(employerTaxNumber)
                  : DOTTED_LINE}
              </Box>
            </Box>
            <Box height="20px" />
            <CorporateSignature orientation="vertical" />
          </Box>
        </Box>
      </Box>
    );
  },
);

MyTaxCombinedTaxCreditApplicationPrint.displayName =
  "CombinedTaxCreditApplicationPrint";

export default MyTaxCombinedTaxCreditApplicationPrint;
